import {
  BodyText,
  Box,
  Card,
  Flex,
  Heading,
  Label,
  Modal,
  ModalContent,
  ModalRoot,
  ModalTrigger,
  RadioGroup,
  RadioTile,
  Strong,
  TextInput,
  UnstyledIconButton,
} from '@utilitywarehouse/hearth-react';
import { InfoMediumIcon } from '@utilitywarehouse/hearth-react-icons';
import { useFormikContext } from 'formik';

import { PAC_NUMBER, STAC_NUMBER } from '@constants/apiConstants';
import { useFieldProps } from '@hooks/useFieldProps';
import { useSyncState } from '@hooks/useSyncState';
import { BooleanSelection } from '@src/types/index';

import { SimSettingsFormValues } from '../../types';
import './styles.scss';

enum ModalContentVariant {
  keepNumber = 'keepNumber',
  cancelContract = 'cancelContract',
}

const modalContent = {
  [ModalContentVariant.keepNumber]: {
    heading: 'Keep an existing number',
    content:
      'If you choose to keep an existing mobile number, we’ll transfer your number when you activate your new SIM and we’ll cancel your contract with your current provider at the same time. This can take a few hours, but you can keep using your old SIM until the transfer is complete.',
  },
  [ModalContentVariant.cancelContract]: {
    heading: 'Cancel your contract',
    content:
      "If you select 'yes', you'll get a new number and we'll ask your provider to cancel your old number after you activate your new UW SIM.",
  },
} as const;

interface LabelModalTriggerProps {
  label: string;
  onClick: () => void;
}

const LabelModalTrigger = ({ label, onClick }: LabelModalTriggerProps) => (
  <BodyText weight="semibold">
    <span>{label}</span>{' '}
    <ModalTrigger>
      <UnstyledIconButton label="" onClick={onClick}>
        <InfoMediumIcon />
      </UnstyledIconButton>
    </ModalTrigger>
  </BodyText>
);

const SimSettingsFormSection = () => {
  const [modalContentVariant, setModalContentVariant] = useSyncState(
    'simForm-modalContent',
    ModalContentVariant.keepNumber
  );
  const [open, setOpen] = useSyncState('simForm-modalOpen', false);
  const { values, errors, handleChange, handleBlur, touched, submitCount } =
    useFormikContext<SimSettingsFormValues>();

  const getFieldProps = useFieldProps<SimSettingsFormValues>({
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    submitCount,
  });

  const handleTooltipClick = (modalContentVariant: ModalContentVariant) => {
    setOpen(true);
    setModalContentVariant(modalContentVariant);
  };

  return (
    <ModalRoot open={open} onOpenChange={setOpen}>
      <Flex asChild direction="column" spacing="lg">
        <section className="simSettingsForm__wrapper">
          <Heading as="h2" size="lg">
            Confirm your SIM settings
          </Heading>
          <BodyText>
            You can also update these settings in your online account after you
            sign up.
          </BodyText>
          <Card
            direction="column"
            spacing="2xl"
            colorScheme="neutralStrong"
            variant="subtle"
          >
            <Flex direction="column" spacing="lg">
              <Heading as="h3">Your mobile number</Heading>
              <Flex direction="column" spacing="xl">
                <RadioGroup
                  {...getFieldProps('isKeepNumber')}
                  direction="row"
                  name="isKeepNumber"
                  helperText="Providing your PAC now saves time later. We will only switch your number after you’ve received your new SIM and activated it."
                  label={
                    <LabelModalTrigger
                      label="Would you like to keep an existing mobile number for this SIM?"
                      onClick={() =>
                        handleTooltipClick(ModalContentVariant.keepNumber)
                      }
                    />
                  }
                  value={values.isKeepNumber}
                  onValueChange={handleChange('isKeepNumber')}
                  data-testid="mobile-sim-select-pac"
                >
                  <RadioTile
                    value={BooleanSelection.Yes}
                    label="Yes"
                    id="mobile-sim-select-pac-yes"
                    flex={{
                      tablet: '0',
                      mobile: '1',
                    }}
                  />
                  <RadioTile
                    value={BooleanSelection.No}
                    label="No"
                    id="mobile-sim-select-pac-no"
                    flex={{
                      tablet: '0',
                      mobile: '1',
                    }}
                  />
                </RadioGroup>
                {values.isKeepNumber === BooleanSelection.Yes && (
                  <>
                    <TextInput
                      {...getFieldProps('keepNumber')}
                      label="Mobile number you’d like to keep"
                      data-testid="mobile-sim-pac-number"
                      required
                    />
                    <Box>
                      <Flex direction="column" asChild>
                        <Label htmlFor="pacNumber">
                          <Strong>PAC</Strong>
                          <BodyText color="secondary">
                            To get your code, text PAC to {PAC_NUMBER} from the
                            number you’d like to keep
                          </BodyText>
                        </Label>
                      </Flex>
                      <TextInput
                        {...getFieldProps('pacNumber')}
                        id="pacNumber"
                        label=""
                        data-testid="mobile-sim-pac-code"
                        required
                      />
                    </Box>
                  </>
                )}
                {values.isKeepNumber === BooleanSelection.No && (
                  <>
                    <RadioGroup
                      {...getFieldProps('isCancelNumber')}
                      name="isCancelNumber"
                      direction="row"
                      label={
                        <LabelModalTrigger
                          label="Would you like us to cancel your contract with your current mobile provider?"
                          onClick={() =>
                            handleTooltipClick(
                              ModalContentVariant.cancelContract
                            )
                          }
                        />
                      }
                      value={values.isCancelNumber}
                      onValueChange={handleChange('isCancelNumber')}
                      data-testid="mobile-sim-select-stac"
                    >
                      <RadioTile
                        value={BooleanSelection.Yes}
                        label="Yes"
                        id="mobile-sim-select-stac-yes"
                        flex={{
                          tablet: '0',
                          mobile: '1',
                        }}
                      />
                      <RadioTile
                        value={BooleanSelection.No}
                        label="No"
                        id="mobile-sim-select-stac-no"
                        flex={{
                          tablet: '0',
                          mobile: '1',
                        }}
                      />
                    </RadioGroup>
                    {values.isCancelNumber === BooleanSelection.Yes && (
                      <>
                        <TextInput
                          {...getFieldProps('cancelNumber')}
                          label="Mobile number you’d like to cancel"
                          data-testid="mobile-sim-stac-number"
                          required
                        />

                        <Box>
                          <Flex direction="column" asChild>
                            <Label htmlFor="stacNumber">
                              <Strong>STAC</Strong>
                              <BodyText color="secondary">
                                To get your code, text STAC to {STAC_NUMBER}{' '}
                                from the number you’d like to cancel
                              </BodyText>
                            </Label>
                          </Flex>
                          <TextInput
                            {...getFieldProps('stacNumber')}
                            data-testid="mobile-sim-stac-code"
                            label=""
                            required
                          />
                        </Box>
                      </>
                    )}
                  </>
                )}
              </Flex>
            </Flex>
            <Flex direction="column" spacing="lg">
              <Heading as="h3">Budget control</Heading>
              <BodyText>
                If you turn budget control <Strong>on</Strong>, you'll need to
                add a top-up to use chargeable services outside your plan like
                roaming outside the EU, or calling premium numbers.
              </BodyText>
              <RadioGroup
                {...getFieldProps('budgetControl')}
                name="budgetControl"
                direction="row"
                label="Would you like budget control on or off for this SIM?"
                value={values.budgetControl}
                onValueChange={handleChange('budgetControl')}
              >
                <RadioTile
                  value={BooleanSelection.Yes}
                  label="On"
                  id="mobile-sim-budget-control-yes"
                  flex={{
                    tablet: '0',
                    mobile: '1',
                  }}
                />
                <RadioTile
                  value={BooleanSelection.No}
                  label="Off"
                  id="mobile-sim-budget-control-no"
                  flex={{
                    tablet: '0',
                    mobile: '1',
                  }}
                />
              </RadioGroup>
            </Flex>
          </Card>
        </section>
      </Flex>
      <Modal
        loading={false}
        heading={modalContent[modalContentVariant].heading}
        hideCloseButton={false}
      >
        <ModalContent>{modalContent[modalContentVariant].content}</ModalContent>
      </Modal>
    </ModalRoot>
  );
};

export { SimSettingsFormSection };
